
// Import utils
import sleep from './sleep';
import uuid from './uuid';

// Types
type status_t = {finished:boolean};
type bool_dic_t = {[id: string]:status_t};

const VERBOSE = false;
const log = VERBOSE ? console.log : () => {};

const assets = require.context(
    "../assets",  // context folder
    true,         // include subdirectories
    /.*/,         // RegExp
    'lazy',       // Lazy, returns a promise
);


/**
 * Preloader class that allows to ensure that some assets are
 * available before using them
 */
export default class Preloader {

    // The dictionary with the assets
    static files: bool_dic_t = {};

    //
    // Main function: preloads a asset and wait to be ready
    //
    public static async preload(fname: string){

        this.preload_inner(fname);

        while(!Preloader.files[fname].finished){
            await sleep(100);
        }
    }

    //
    // Preload a full folder of assets
    //
    public static async preloadFolder(folder: string){

        // Get the files in that folder
        const fnames = assets
            .keys()
            .filter((fname => fname.startsWith(`./${folder}/`)) )
            .map(fname => fname.substr(2));

        // Wait for all the assets
        const waits = fnames.map(fname => this.preload(fname));
        await Promise.all(waits);

    }

    //
    // Inner function to preload an asset
    //
    private static async preload_inner(fname:string){

        // Skip already preloaded files
        if(Preloader.files[fname]) return;

        // Create status object for this file
        const status = {finished:false};
        Preloader.files[fname] = status;

        // Get the url of the asset
        log(`Preloading ${fname}`);
        const url = await assets('./' + fname);
        log(`Preloading ${fname} -> ${url}`);

        // There are two methods: the regular and the ugly one
        if(Preloader.isSupported()){
            await this.preload_inner_regular(status, fname, url);
        } else {
            await this.preload_inner_ugly(status, fname, url);
        }

    }

    //
    // The regular method uses the link rel="preload"
    // This works in Chrome but not in Firefox
    //
    private static async preload_inner_regular(status:status_t, fname:string, url:string){

        // Create a link tag
        // https://developer.mozilla.org/en-US/docs/Web/API/HTMLLinkElement
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = this.getLinkType(fname);
        link.href = url;
        document.body.appendChild(link);

        // Callback for success and fail
        link.onload = () => {
            log('Preloaded', fname);
            status.finished = true;
        };
        link.onerror = (evt) => {
            console.error('Error preloading', fname, evt);
            status.finished = true;
        };

    }

    //
    // Get the file type based on the extension
    //
    private static getLinkType(fname: string){

        const ext = fname.toLowerCase().split('.').pop();

        switch(ext){
            case 'ttf':
                return 'font';
            default:
                return 'image';
        }

    }

    //
    // Alternative version for browsers not supporting the link rel="preload"
    //
    private static async preload_inner_ugly(status:status_t, fname:string, url:string){

        const type = this.getLinkType(fname);

        switch(type){
            case 'image':
                return await this.preload_inner_ugly_image(status, fname, url);
            case 'font':
                return await this.preload_inner_ugly_font(status, fname, url);
        }


    }

    //
    // Preload a image
    //
    private static async preload_inner_ugly_image(status:status_t, fname:string, url:string){

        log("Preload is not supported, using ugly alternative for image")

        // Create 
        const img = document.createElement('img');
        img.src = url;

        img.onload = () => {
            log('Preloaded', fname);
            status.finished = true;
        };
        img.onerror = (evt) => {
            console.error('Error preloading', fname, evt);
            status.finished = true;
        };

    }

    //
    // There is no real preload wait for font. Just use them in a hidden div
    // and wait the browser is ready when they are needed.
    //
    private static async preload_inner_ugly_font(status:status_t, fname:string, url:string){

        console.warn("Preload is not supported, using ugly alternative for font");
        status.finished = true;

        //
        // Add the style to the header
        //
        const font = 'preload-font-' + uuid();
        const css = `
            @font-face {
                font-family: "${font}";
                src: url("${url}");
            }
        `;
        const style = document.createElement('style');
        document.head.appendChild(style);
        style.type = 'text/css';
        style.appendChild(document.createTextNode(css));
        
        //
        // Add a fake text to force font loading
        //
        const div = document.createElement('div');
        const text = document.createTextNode("abcefghijklmopqrstuvwxyz"); 
        div.appendChild(text);
        div.style.cssText = `font-family: ${font}; visibility: hidden;`;
        document.body.appendChild(div);

    }

    //
    // Check if link rel="preload" is supported
    //
    public static isSupported(){

        // https://gist.github.com/igrigorik/a02f2359f3bc50ca7a9c
        const DOMTokenListSupports = function(tokenList:any, token:string) {
            if (!tokenList || !tokenList.supports) {
                return;
            }
            try {
                return tokenList.supports(token);
            } catch (e) {
                if (e instanceof TypeError) {
                    console.log("The DOMTokenList doesn't have a supported tokens list");
                } else {
                    console.error("That shouldn't have happened");
                }
            }
        };
          
        // test if preload is supported
        return !!DOMTokenListSupports(document.createElement("link").relList, "preload"); 

    }
    
}

